import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';

import { useState } from 'react';

import { Space, Typography, Upload, message, Alert } from 'antd';

import DocumentCard from './DocumentCard';
import UploadSummary from './UploadSummary';
import { DownloadViaLink, getError } from '../../clientUtils';

const { Text, Title } = Typography;

const CustomUpload = ({
  title,
  description,
  sampleDownloadText,
  sampleDownloadLink,
  sampleDownloadFileName,
  onSampleDownload,
  accept,
  files,
  onRemove,
  validator,
  onUpload,
  multiple,
  required = true,
  deleteDisabled = false,
  uploadingText = 'Uploading...'
}) => {
  const [loading, setLoading] = useState(false);
  const [sampleDownloading, setSampleDownloading] = useState(false);

  const handleSampleDownload = async () => {
    if (onSampleDownload) {
      setSampleDownloading(true);
      try {
        await onSampleDownload();
      } catch (error) {
        message.error(getError(error));
      } finally {
        setSampleDownloading(false);
      }
      return;
    }

    DownloadViaLink(sampleDownloadLink, sampleDownloadFileName || title);
  };

  const uploaderProps = {
    beforeUpload(file) {
      setLoading(true);
      const upload = async (uploadFile) => {
        try {
          if (validator) validator(uploadFile, accept);
          await onUpload(uploadFile);
        } catch (error) {
          message.error(getError(error));
        } finally {
          setLoading(false);
        }
      };

      upload(file);
      return false;
    },
    accept,
    showUploadList: false,
    disabled: loading,
    listType: 'picture-card'
  };

  const uploader = (
    <Upload
      accept={uploaderProps.accept}
      beforeUpload={uploaderProps.beforeUpload}
      disabled={uploaderProps.disabled}
      listType={uploaderProps.listType}
      showUploadList={uploaderProps.showUploadList}>
      <Space direction="vertical">
        {loading ? <LoadingOutlined /> : <PlusOutlined />}
        <div style={loading ? { fontSize: 12, lineHeight: 1.2 } : undefined}>{loading ? uploadingText : 'Upload'}</div>
      </Space>
    </Upload>
  );

  const fileListView = files?.map((file, index) => {
    const { summary, link, messages } = file || {};
    const { failure } = messages || {};

    return (
      <Space key={index} direction="vertical" size="middle">
        <DocumentCard
          deleteDisabled={deleteDisabled}
          file={file}
          handleRemove={onRemove}
          link={link}
          name={`${title} (${index + 1})`}
        />
        {!!summary && <UploadSummary fileUrl={link} summary={summary} />}
        {!!failure?.length &&
          failure?.map((error, errIdx) => <Alert key={errIdx} closable message={error} showIcon type="error" />)}
      </Space>
    );
  });

  return (
    <section className="custom-upload">
      <Title className={required ? 'required-after' : ''} level={5}>
        {title}
      </Title>
      <Space direction="vertical" size="middle">
        <Space className="description">
          <Text>{description}</Text>
          <Text
            className="pointer fw-500"
            onClick={sampleDownloading ? undefined : handleSampleDownload}
            underline
            style={sampleDownloading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}>
            {sampleDownloading ? 'Downloading...' : sampleDownloadText}
          </Text>
        </Space>
        <Space align="start">
          {(!!multiple || !files?.length) && uploader}
          {!!files?.length && fileListView}
        </Space>
      </Space>
    </section>
  );
};

export default CustomUpload;
